import { type Document } from '@contentful/rich-text-types';
import type { IGatsbyImageData } from 'gatsby-plugin-image';
import { GatsbyImage } from 'gatsby-plugin-image';
import type { BoxProps } from 'rebass/styled-components';

import { RichText, Video, withErrorBoundary } from '../../components';

import { StyledContainerBrandX, StyledImageWrapperBrandX, StyledTextWrapperBrandX } from './Styled';

export interface ImageAndTextModuleProps extends BoxProps {
  image?: {
    alt?: string;
    title: string;
    asset: {
      gatsbyImageData: IGatsbyImageData;
    };
    roundedCornerPosition?: 'Top Left' | 'Bottom Left' | 'Top Right' | 'Bottom Right';
  };
  videoUrl?: string;
  richText: {
    json: Document;
  };
  alignImageRight?: boolean;
  textAlignment?: 'Left' | 'Center' | 'Right';
  className?: string;
}

const ImageAndTextModule = ({
  className,
  image,
  videoUrl,
  richText,
  alignImageRight,
  textAlignment,
}: ImageAndTextModuleProps) => {
  const getRoundedCornerClass = (position?: 'Top Left' | 'Bottom Left' | 'Top Right' | 'Bottom Right'): string => {
    if (!position) return '';

    const classMap = {
      'Bottom Left': 'rounded-bl-[6rem]',
      'Bottom Right': 'rounded-br-[6rem]',
      'Top Left': 'rounded-tl-[6rem]',
      'Top Right': 'rounded-tr-[6rem]',
    };

    return `${classMap[position]} overflow-hidden`;
  };

  return (
    <StyledContainerBrandX alignImageRight={alignImageRight} className={className}>
      <StyledImageWrapperBrandX
        alignImageRight={alignImageRight}
        className={getRoundedCornerClass(image?.roundedCornerPosition)}
      >
        {image?.asset && (
          <GatsbyImage
            alt={image?.alt ? image.alt : image.title}
            image={image.asset?.gatsbyImageData}
            loading="eager"
            style={{ width: '100%' }}
            objectFit="contain"
          />
        )}
        {!image && videoUrl && <Video videoUrl={videoUrl} />}
      </StyledImageWrapperBrandX>
      <StyledTextWrapperBrandX alignImageRight={alignImageRight}>
        <RichText alignment={textAlignment} data={richText.json} isImageAndText />
      </StyledTextWrapperBrandX>
    </StyledContainerBrandX>
  );
};

export default withErrorBoundary(ImageAndTextModule, { componentName: 'ImageAndTextModule' });
